import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, CheckCircle, Command } from "@phosphor-icons/react";
import { toast } from "sonner";

const LABELS = ["", "Very dissatisfied", "Dissatisfied", "Neutral", "Satisfied", "Very satisfied"];

export default function FeedbackSurvey() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [score, setScore] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/incidents/${id}`)
      .then(r => {
        setTicket(r.data);
        if (r.data.existing_csat) { setScore(r.data.existing_csat.score); setComment(r.data.existing_csat.comment || ""); }
      })
      .catch(() => setNotFound(true));
  }, [id]);

  const submit = async () => {
    if (!score) return toast.error("Please pick a rating");
    setSubmitting(true);
    try {
      await api.post(`/public/incidents/${id}/csat`, { score, comment });
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-6" data-testid="feedback-survey-view">
      <div className="flex items-center gap-2 mb-8">
        <div className="h-7 w-7 grid place-items-center bg-primary text-primary-foreground rounded-sm"><Command size={16} weight="bold" /></div>
        <div className="font-display font-bold tracking-tight text-lg">TRAK</div>
      </div>

      {notFound ? (
        <Card className="p-8 border-border rounded-sm max-w-md w-full text-center">
          <div className="text-muted-foreground">This survey link is invalid or the ticket no longer exists.</div>
        </Card>
      ) : !ticket ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : done ? (
        <Card className="p-10 border-border rounded-sm max-w-md w-full text-center" data-testid="feedback-thanks">
          <CheckCircle size={48} className="text-primary mx-auto mb-4" weight="fill" />
          <h1 className="font-display text-2xl font-bold mb-2">Thank you!</h1>
          <p className="text-sm text-muted-foreground">Your feedback on <span className="font-mono text-primary">{ticket.number}</span> has been recorded. It helps us improve our service.</p>
        </Card>
      ) : (
        <Card className="p-8 border-border rounded-sm max-w-md w-full">
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Resolution Feedback</div>
          <h1 className="font-display text-2xl font-bold mb-1">How did we do?</h1>
          <p className="text-sm text-muted-foreground mb-6">Ticket <span className="font-mono text-primary">{ticket.number}</span> — {ticket.title}</p>

          <div className="flex items-center justify-center gap-2 mb-2" data-testid="csat-stars">
            {[1,2,3,4,5].map(n => (
              <button
                key={n}
                data-testid={`csat-star-${n}`}
                onMouseEnter={()=>setHover(n)}
                onMouseLeave={()=>setHover(0)}
                onClick={()=>setScore(n)}
                className="transition-transform hover:scale-110"
              >
                <Star size={40} weight={(hover || score) >= n ? "fill" : "regular"} className={(hover || score) >= n ? "text-primary" : "text-muted-foreground"} />
              </button>
            ))}
          </div>
          <div className="text-center text-sm text-muted-foreground h-5 mb-4">{LABELS[hover || score]}</div>

          <Textarea data-testid="csat-comment" className="rounded-sm min-h-[100px] mb-4" placeholder="Anything you'd like to add? (optional)" value={comment} onChange={(e)=>setComment(e.target.value)} />
          <Button data-testid="csat-submit" onClick={submit} disabled={submitting || !score} className="w-full rounded-sm font-mono uppercase text-xs tracking-widest">
            {submitting ? "Submitting…" : "Submit Feedback"}
          </Button>
        </Card>
      )}
    </div>
  );
}
